import { Component } from '@theme/component';

/**
 * Orchard Help Center — interactive behavior for the FAQ page.
 *
 * Handles:
 *  - Client-side search filter across pills, categories, and articles
 *  - Category card expand/collapse
 *  - Article modal (populates title + content from inline <template>)
 *  - Deep-linking via URL hash (#a=<slug>) + "Copy link" button
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} [searchInput]
 * @property {HTMLElement} [emptyMessage]
 * @property {HTMLElement} [categoriesGrid]
 * @property {HTMLDialogElement} [articleModal]
 * @property {HTMLElement} [articleTitle]
 * @property {HTMLElement} [articleContent]
 * @property {HTMLButtonElement} [copyLinkButton]
 * @property {HTMLElement} [copyLinkLabel]
 *
 * @extends Component<Refs>
 */
export class OrchardHelpCenter extends Component {
  /** @type {HTMLElement[]} */
  #categories = [];
  /** @type {HTMLElement[]} */
  #pills = [];
  /** @type {HTMLElement | null} */
  #pillsSection = null;
  /** @type {HTMLElement | null} */
  #categoriesSection = null;
  #previousScrollY = 0;
  /** @type {string | null} */
  #currentSlug = null;
  /** @type {number | undefined} */
  #copyResetTimer;

  static HASH_PREFIX = '#a=';

  connectedCallback() {
    super.connectedCallback();

    this.#categories = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-orchard-category]'))
    );
    this.#pills = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-orchard-pill]'))
    );
    this.#pillsSection = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-orchard-pills-section]')
    );
    this.#categoriesSection = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-orchard-categories-section]')
    );

    // Search input
    const input = this.refs.searchInput;
    if (input) {
      input.addEventListener('input', this.#onSearchInput);
    }

    // Category toggles
    for (const btn of this.querySelectorAll('[data-orchard-category-toggle]')) {
      btn.addEventListener('click', this.#onCategoryToggle);
    }

    // Article buttons (open modal)
    for (const btn of this.querySelectorAll('[data-orchard-article]')) {
      btn.addEventListener('click', this.#onArticleClick);
    }

    // Modal dismissal via backdrop click / escape
    const modal = this.refs.articleModal;
    if (modal) {
      modal.addEventListener('click', this.#onModalClick);
      modal.addEventListener('cancel', this.#onModalCancel);
    }

    // Deep-link: if the URL arrives with #a=<slug>, open that article
    window.addEventListener('popstate', this.#onPopState);
    this.#openFromHash();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const input = this.refs.searchInput;
    if (input) input.removeEventListener('input', this.#onSearchInput);
    window.removeEventListener('popstate', this.#onPopState);
    if (this.#copyResetTimer) window.clearTimeout(this.#copyResetTimer);
  }

  /** @param {Event} event */
  #onSearchInput = (event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    const q = (target.value || '').trim().toLowerCase();
    this.#applyFilter(q);
  };

  /** @param {string} q */
  #applyFilter = (q) => {
    if (q === '') {
      // Reset everything
      for (const pill of this.#pills) pill.hidden = false;
      for (const cat of this.#categories) {
        cat.hidden = false;
        this.#collapseCategory(cat);
        for (const btn of cat.querySelectorAll('[data-orchard-article]')) {
          const item = /** @type {HTMLElement | null} */ (
            btn.closest('.orchard-help-center__article-item')
          );
          if (item) item.hidden = false;
        }
      }
      if (this.#pillsSection) this.#pillsSection.hidden = false;
      if (this.#categoriesSection) this.#categoriesSection.hidden = false;
      if (this.refs.emptyMessage) this.refs.emptyMessage.hidden = true;
      return;
    }

    // Filter pills
    let pillVisible = 0;
    for (const pill of this.#pills) {
      const match = (pill.dataset.title || '').toLowerCase().includes(q);
      pill.hidden = !match;
      if (match) pillVisible++;
    }
    if (this.#pillsSection) this.#pillsSection.hidden = pillVisible === 0;

    // Filter categories + articles
    let categoryVisible = 0;
    for (const cat of this.#categories) {
      const title = (cat.dataset.title || '').toLowerCase();
      const titleMatch = title.includes(q);

      let articleMatchCount = 0;
      const articles = cat.querySelectorAll('[data-orchard-article]');
      for (const btn of articles) {
        const button = /** @type {HTMLElement} */ (btn);
        const item = /** @type {HTMLElement | null} */ (
          button.closest('.orchard-help-center__article-item')
        );
        const articleTitle = (button.dataset.title || '').toLowerCase();
        const match = titleMatch || articleTitle.includes(q);
        if (item) item.hidden = !match;
        if (match) articleMatchCount++;
      }

      const visible = titleMatch || articleMatchCount > 0;
      cat.hidden = !visible;
      if (visible) {
        categoryVisible++;
        // Auto-expand when an article matched so users see results
        if (articleMatchCount > 0) this.#expandCategory(cat);
      }
    }
    if (this.#categoriesSection) this.#categoriesSection.hidden = categoryVisible === 0;

    const anyVisible = pillVisible > 0 || categoryVisible > 0;
    if (this.refs.emptyMessage) this.refs.emptyMessage.hidden = anyVisible;
  };

  /** @param {Event} event */
  #onCategoryToggle = (event) => {
    const btn = /** @type {HTMLElement} */ (event.currentTarget);
    const cat = /** @type {HTMLElement | null} */ (
      btn.closest('[data-orchard-category]')
    );
    if (!cat) return;
    if (cat.classList.contains('is-expanded')) {
      this.#collapseCategory(cat);
    } else {
      this.#expandCategory(cat);
    }
  };

  /** @param {HTMLElement} cat */
  #expandCategory = (cat) => {
    cat.classList.add('is-expanded');
    const btn = cat.querySelector('[data-orchard-category-toggle]');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    const body = /** @type {HTMLElement | null} */ (
      cat.querySelector('.orchard-help-center__category-body')
    );
    if (body) body.hidden = false;
  };

  /** @param {HTMLElement} cat */
  #collapseCategory = (cat) => {
    cat.classList.remove('is-expanded');
    const btn = cat.querySelector('[data-orchard-category-toggle]');
    if (btn) btn.setAttribute('aria-expanded', 'false');
    const body = /** @type {HTMLElement | null} */ (
      cat.querySelector('.orchard-help-center__category-body')
    );
    if (body) body.hidden = true;
  };

  /** @param {Event} event */
  #onArticleClick = (event) => {
    const btn = /** @type {HTMLElement} */ (event.currentTarget);
    this.#openArticleByButton(btn, { updateUrl: true });
  };

  /**
   * @param {HTMLElement} btn
   * @param {{ updateUrl?: boolean }} [opts]
   */
  #openArticleByButton = (btn, opts = {}) => {
    const title = btn.dataset.title || '';
    const slug = btn.dataset.slug || null;
    const contentId = btn.dataset.contentId;
    const tmpl = contentId ? document.getElementById(contentId) : null;
    const html = tmpl instanceof HTMLTemplateElement
      ? tmpl.innerHTML
      : '<p>Content not available.</p>';
    this.#openArticle(title, html, slug, opts.updateUrl !== false);
  };

  /**
   * @param {string} title
   * @param {string} html
   * @param {string | null} slug
   * @param {boolean} updateUrl
   */
  #openArticle = (title, html, slug, updateUrl) => {
    const modal = this.refs.articleModal;
    if (!modal) return;
    if (this.refs.articleTitle) this.refs.articleTitle.textContent = title;
    if (this.refs.articleContent) this.refs.articleContent.innerHTML = html;

    this.#currentSlug = slug;
    this.#resetCopyButton();

    if (updateUrl && slug) {
      const newUrl = `${window.location.pathname}${window.location.search}${OrchardHelpCenter.HASH_PREFIX}${encodeURIComponent(slug)}`;
      window.history.pushState({ orchardFaq: slug }, '', newUrl);
    }

    if (!modal.open) {
      this.#previousScrollY = window.scrollY;
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;
      modal.showModal();
    }

    if (this.refs.articleContent) {
      this.refs.articleContent.scrollTop = 0;
    }
  };

  closeArticle = () => {
    const modal = this.refs.articleModal;
    if (!modal || !modal.open) return;
    modal.close();

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    // Clear the hash from the URL when user closes the modal
    if (window.location.hash.startsWith(OrchardHelpCenter.HASH_PREFIX)) {
      const cleanUrl = `${window.location.pathname}${window.location.search}`;
      window.history.pushState({}, '', cleanUrl);
    }
    this.#currentSlug = null;
  };

  #openFromHash = () => {
    const hash = window.location.hash;
    if (!hash.startsWith(OrchardHelpCenter.HASH_PREFIX)) return;
    const slug = decodeURIComponent(hash.slice(OrchardHelpCenter.HASH_PREFIX.length));
    if (!slug) return;
    const btn = /** @type {HTMLElement | null} */ (
      this.querySelector(`[data-orchard-article][data-slug="${CSS.escape(slug)}"]`)
    );
    if (!btn) return;
    // Also expand the containing category so context is visible behind the modal
    const cat = /** @type {HTMLElement | null} */ (btn.closest('[data-orchard-category]'));
    if (cat) this.#expandCategory(cat);
    this.#openArticleByButton(btn, { updateUrl: false });
  };

  #onPopState = () => {
    // User used browser back/forward — re-sync modal state with the URL
    if (window.location.hash.startsWith(OrchardHelpCenter.HASH_PREFIX)) {
      this.#openFromHash();
    } else {
      const modal = this.refs.articleModal;
      if (modal && modal.open) {
        modal.close();
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
      }
      this.#currentSlug = null;
    }
  };

  copyArticleLink = async () => {
    const slug = this.#currentSlug;
    if (!slug) return;
    const url = `${window.location.origin}${window.location.pathname}${OrchardHelpCenter.HASH_PREFIX}${encodeURIComponent(slug)}`;
    try {
      await navigator.clipboard.writeText(url);
      this.#showCopyFeedback(true);
    } catch (err) {
      // Fallback for older browsers / denied permissions
      const ok = this.#fallbackCopy(url);
      this.#showCopyFeedback(ok);
    }
  };

  /**
   * @param {string} text
   * @returns {boolean}
   */
  #fallbackCopy = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (_e) {
      ok = false;
    }
    document.body.removeChild(textarea);
    return ok;
  };

  /** @param {boolean} success */
  #showCopyFeedback = (success) => {
    const btn = this.refs.copyLinkButton;
    const label = this.refs.copyLinkLabel;
    if (!btn) return;
    const idleIcon = btn.querySelector('[data-copy-idle]');
    const doneIcon = btn.querySelector('[data-copy-done]');

    btn.classList.add('is-copied');
    if (label) label.textContent = (success ? btn.dataset.textDone : btn.dataset.textFailed) ?? '';
    if (idleIcon instanceof HTMLElement) idleIcon.hidden = true;
    if (doneIcon instanceof HTMLElement) doneIcon.hidden = false;

    if (this.#copyResetTimer) window.clearTimeout(this.#copyResetTimer);
    this.#copyResetTimer = window.setTimeout(() => this.#resetCopyButton(), 1800);
  };

  #resetCopyButton = () => {
    const btn = this.refs.copyLinkButton;
    const label = this.refs.copyLinkLabel;
    if (!btn) return;
    btn.classList.remove('is-copied');
    if (label) label.textContent = btn.dataset.textIdle ?? '';
    const idleIcon = btn.querySelector('[data-copy-idle]');
    const doneIcon = btn.querySelector('[data-copy-done]');
    if (idleIcon instanceof HTMLElement) idleIcon.hidden = false;
    if (doneIcon instanceof HTMLElement) doneIcon.hidden = true;
    if (this.#copyResetTimer) {
      window.clearTimeout(this.#copyResetTimer);
      this.#copyResetTimer = undefined;
    }
  };

  /** @param {MouseEvent} event */
  #onModalClick = (event) => {
    const modal = this.refs.articleModal;
    if (!modal) return;
    // Close when clicking on the dialog element itself (backdrop area)
    if (event.target === modal) {
      this.closeArticle();
    }
  };

  /** @param {Event} event */
  #onModalCancel = (event) => {
    // Escape key triggers 'cancel' — intercept so we can restore scroll
    event.preventDefault();
    this.closeArticle();
  };
}

if (!customElements.get('orchard-help-center')) {
  customElements.define('orchard-help-center', OrchardHelpCenter);
}
